// apps/cop/src/host/entrance.ts
// CONTRACT:C1-MAP-CLIENT.1.0
//
// COP — the pre-COP ENTRANCE ROUTING decision (splash | login | cop). Pure.
//
// WHY THIS EXISTS (the Google-bounce bug, prod 2026-07-06): the Phase 3 splash
// ("Choose your entrance", SplashScreen) was inserted IN FRONT of the LoginScreen
// — but the "Sign in with Google" redirect completer lives in LoginScreen's mount
// effect (LoginScreen.tsx → completeGoogleRedirect). On the post-Google boot the
// app lands on `?code&state` with NO session yet (the code is exchanged for a
// session AFTER landing, server-side at the IdP), so App's `!session && !entered`
// gate showed the splash and the exchange never ran: the operator was bounced back
// to "Choose your entrance". THE RULE: an OAuth callback landing must route to the
// LOGIN screen so its effect can complete (or surface) the exchange.
//
// FAIL-CLOSED INVARIANTS:
//   · "cop" REQUIRES a real session (restored unexpired via tdflite.restore()'s
//     expiry gate, or freshly minted by a login/exchange). It is NEVER inferred
//     from the URL — a `?code` landing routes to "login", not "cop", because no
//     session exists until the TRUSTED IdP redeems the code.
//   · An absent / expired / unparseable stored session still lands on the splash
//     exactly as before (restore() returns null and drops the bad entry).
//   · A `?error` landing (Google denial/cancel) also routes to "login" so the
//     failure is SURFACED there — never silently swallowed by the splash.
//
// CONSUMPTION (App.tsx's entrance gate): App implements this decision as
//   session (tdflite.restore) → cop; else entered → login; else splash
// with `entered` INITIALIZED to isOAuthCallbackLanding(window.location.href) —
//   const [entered, setEntered] = useState(() => isOAuthCallbackLanding(window.location.href));
// i.e. an OAuth landing counts as "already chose the entrance" (the operator DID:
// they clicked "Sign in with Google" before the redirect). Initializing state —
// rather than deriving the route per render — keeps the "← Entrance" back button
// a real true→false transition after a failed exchange (a derived route would hit
// React's same-value setState bailout: setEntered(false) while already false).
// decideEntranceRoute() below is the tested single-source spec of that composed
// boot decision; entrance.test.ts proves it against the REAL tdflite.restore()/
// federateGoogle persistence semantics.

import type { Session } from "./tdflite.ts";
import { readGoogleCallback } from "./google-oidc.ts";

export type EntranceRoute = "splash" | "login" | "cop";

/** True when this boot is an OAuth redirect landing: Google bounced us back with
 *  `?code` (success — must reach LoginScreen's completer) or `?error` (denial/
 *  cancel — must reach LoginScreen so the error is surfaced). Query params only;
 *  a `#@…` urlState hash fragment never triggers this. Pure. */
export function isOAuthCallbackLanding(href: string): boolean {
  const cb = readGoogleCallback(href);
  return cb.code !== undefined || cb.error !== undefined;
}

/** The boot-time entrance decision App.tsx implements (session gate + the
 *  `entered` initializer — see CONSUMPTION above):
 *
 *    session (real, unexpired)            → "cop"
 *    no session + entered-or-OAuth-landing → "login"
 *    no session otherwise                  → "splash"
 *
 *  `session` must come from tdflite.restore() (expiry-gated) or a live login/
 *  exchange — never from peek(). A valid session WINS over a stray `?code` on the
 *  URL (nothing re-processes a code while signed in; codes are single-use at the
 *  IdP anyway). */
export function decideEntranceRoute(i: {
  /** The current session: tdflite.restore() at boot, or App's live login state. */
  session: Session | null;
  /** The operator clicked "Enter the TDF COP" (App's `entered` state). */
  entered: boolean;
  /** window.location.href at decision time. */
  href: string;
}): EntranceRoute {
  if (i.session) return "cop";
  if (i.entered || isOAuthCallbackLanding(i.href)) return "login";
  return "splash";
}
